import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Badge } from "@/components/ui/badge";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useSharing } from "@/hooks/use-sharing";
import { User, Users, Filter, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export type DataViewMode = "all" | "mine" | "shared";

interface DataViewContextValue {
  viewMode: DataViewMode;
  setViewMode: (mode: DataViewMode) => void;
  hasSharedAccess: boolean;
  sharedUserIds: string[];
  currentUserId: string | null;
}

const DataViewContext = createContext<DataViewContextValue | null>(null);

/**
 * Provider for data view filtering context
 */
export function DataViewProvider({ children }: { children: ReactNode }) {
  const [viewMode, setViewMode] = useState<DataViewMode>("all");
  const { activeAccess, currentUserId } = useSharing();
  
  const hasSharedAccess = activeAccess.length > 0;
  const sharedUserIds = activeAccess.map(s => s.owner_id);

  // Reset to "all" if user loses shared access
  useEffect(() => {
    if (!hasSharedAccess && viewMode === "shared") {
      setViewMode("all");
    }
  }, [hasSharedAccess, viewMode]);

  return (
    <DataViewContext.Provider 
      value={{ 
        viewMode, 
        setViewMode, 
        hasSharedAccess,
        sharedUserIds,
        currentUserId
      }}
    >
      {children}
    </DataViewContext.Provider>
  );
}

/**
 * Hook to access data view context
 */
export function useDataView() {
  const context = useContext(DataViewContext);
  if (!context) {
    throw new Error("useDataView must be used within DataViewProvider");
  }
  return context;
}

/**
 * Helper to filter data based on view mode
 */
export function useFilteredData<T extends { user_id?: string }>(
  data: T[] | undefined
): T[] {
  const { viewMode, currentUserId, sharedUserIds } = useDataView();
  
  if (!data) return [];
  
  switch (viewMode) {
    case "mine":
      return data.filter(item => item.user_id === currentUserId);
    case "shared":
      return data.filter(item => item.user_id && sharedUserIds.includes(item.user_id));
    case "all":
    default:
      return data;
  }
}

interface DataViewToggleProps {
  className?: string;
  size?: "sm" | "default";
}

/**
 * Toggle group for switching between data view modes
 */
export function DataViewToggle({ className, size = "default" }: DataViewToggleProps) {
  const { viewMode, setViewMode, hasSharedAccess, activeAccess } = useDataView() as DataViewContextValue & { activeAccess: any[] };
  
  if (!hasSharedAccess) {
    return null;
  }

  return (
    <ToggleGroup 
      type="single" 
      value={viewMode} 
      onValueChange={(v) => v && setViewMode(v as DataViewMode)}
      className={cn("border rounded-lg p-1", className)}
    >
      <ToggleGroupItem 
        value="all" 
        aria-label="Ver todos os dados"
        className={cn(
          size === "sm" && "h-7 px-2 text-xs"
        )}
      >
        <Users className={cn("mr-1", size === "sm" ? "h-3 w-3" : "h-4 w-4")} />
        Todos
      </ToggleGroupItem>
      <ToggleGroupItem 
        value="mine" 
        aria-label="Ver apenas meus dados"
        className={cn(
          size === "sm" && "h-7 px-2 text-xs"
        )}
      >
        <User className={cn("mr-1", size === "sm" ? "h-3 w-3" : "h-4 w-4")} />
        Meus
      </ToggleGroupItem>
      <ToggleGroupItem 
        value="shared" 
        aria-label="Ver dados compartilhados"
        className={cn(
          size === "sm" && "h-7 px-2 text-xs"
        )}
      >
        <Users className={cn("mr-1", size === "sm" ? "h-3 w-3" : "h-4 w-4")} />
        Compartilhados
      </ToggleGroupItem>
    </ToggleGroup>
  );
}

/**
 * Dropdown menu alternative for data view filtering
 */
export function DataViewDropdown({ className }: { className?: string }) {
  const { viewMode, setViewMode, hasSharedAccess } = useDataView();
  
  if (!hasSharedAccess) {
    return null;
  }

  const labels: Record<DataViewMode, string> = {
    all: "Todos os dados",
    mine: "Meus dados",
    shared: "Compartilhados",
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className={cn("gap-2", className)}>
          <Filter className="h-4 w-4" />
          {labels[viewMode]}
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Visualizar</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={viewMode} onValueChange={(v) => setViewMode(v as DataViewMode)}>
          <DropdownMenuRadioItem value="all">
            <Users className="h-4 w-4 mr-2" />
            Todos os dados
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="mine">
            <User className="h-4 w-4 mr-2" />
            Apenas meus dados
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="shared">
            <Users className="h-4 w-4 mr-2" />
            Apenas compartilhados
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Simple badge showing current view mode
 */
export function DataViewBadge() {
  const { viewMode, hasSharedAccess } = useDataView();
  
  if (!hasSharedAccess || viewMode === "all") {
    return null;
  }

  return (
    <Badge variant="secondary" className="gap-1">
      {viewMode === "mine" ? (
        <>
          <User className="h-3 w-3" />
          Meus dados
        </>
      ) : (
        <>
          <Users className="h-3 w-3" />
          Compartilhados
        </>
      )}
    </Badge>
  );
}
